import { useCallback, useEffect, useRef, useState } from 'react';
import type { MutableRefObject, PointerEvent as ReactPointerEvent, RefObject } from 'react';
import { OVERLAY_SCROLL_AXES, computeThumbLayout } from './overlayScrollAxis';
import type { OverlayScrollAxis, OverlayScrollAxisAdapter } from './overlayScrollAxis';

// Hidden native bar + a thumb painted over the content: zero layout cost. Per-frame geometry is
// written straight to the thumb's style, so scrolling never re-renders the host.

const MIN_THUMB_LENGTH_PX = 24;
const DEFAULT_TRACK_INSET_PX = 2;
const HIDE_DELAY_MS = 900;
// Pointer travel within this band of the region edge the thumb rides along re-reveals it.
const EDGE_REVEAL_ZONE_PX = 16;
// Sub-pixel rounding can report a phantom ~1px overflow; ignore it.
const OVERFLOW_EPSILON_PX = 1;

interface ThumbGeometry {
    maxScroll: number;
    maxThumbOffset: number;
}

interface DragState {
    pointerId: number;
    startPointer: number;
    startOffset: number;
}

type GeometryRef = MutableRefObject<ThumbGeometry | null>;
type DragRef = MutableRefObject<DragState | null>;
type FlagRef = MutableRefObject<boolean>;

export interface OverlayScrollbarThumbHandlers {
    onPointerDown: (event: ReactPointerEvent<HTMLDivElement>) => void;
    onPointerMove: (event: ReactPointerEvent<HTMLDivElement>) => void;
    onPointerUp: (event: ReactPointerEvent<HTMLDivElement>) => void;
    onPointerCancel: (event: ReactPointerEvent<HTMLDivElement>) => void;
    onPointerEnter: () => void;
    onPointerLeave: () => void;
}

export interface OverlayScrollbar {
    // State, not a ref: the region can remount, and a ref-keyed effect would watch the dead node.
    setScrollElement: (node: HTMLDivElement | null) => void;
    scrollElement: HTMLDivElement | null;
    axis: OverlayScrollAxis;
    thumbRef: RefObject<HTMLDivElement>;
    // `false` keeps the thumb inert and unpainted.
    hasOverflow: boolean;
    isVisible: boolean;
    thumbHandlers: OverlayScrollbarThumbHandlers;
}

export interface OverlayScrollbarOptions {
    // Inset at both track ends; raise it to clear rounded card corners. Default 2.
    trackInsetPx?: number;
    axis?: OverlayScrollAxis;
}

interface ThumbFade {
    isVisible: boolean;
    reveal: () => void;
    cancelHideTimer: () => void;
    scheduleHide: () => void;
    pin: () => void;
    release: () => void;
}

function useThumbFade(geometryRef: GeometryRef, dragRef: DragRef, hoveringRef: FlagRef): ThumbFade {
    const [isVisible, setIsVisible] = useState(false);
    const hideTimerRef = useRef<number | null>(null);

    const cancelHideTimer = useCallback(() => {
        if (hideTimerRef.current !== null) {
            window.clearTimeout(hideTimerRef.current);
            hideTimerRef.current = null;
        }
    }, []);

    const scheduleHide = useCallback(() => {
        cancelHideTimer();
        hideTimerRef.current = window.setTimeout(() => {
            hideTimerRef.current = null;
            setIsVisible(false);
        }, HIDE_DELAY_MS);
    }, [cancelHideTimer]);

    // Hover and drag pin the thumb visible; otherwise it fades out after HIDE_DELAY_MS.
    const reveal = useCallback(() => {
        if (!geometryRef.current) {
            return;
        }
        setIsVisible(true);
        if (hoveringRef.current || dragRef.current) {
            cancelHideTimer();
            return;
        }
        scheduleHide();
    }, [geometryRef, dragRef, hoveringRef, cancelHideTimer, scheduleHide]);

    const pin = useCallback(() => {
        cancelHideTimer();
        setIsVisible(true);
    }, [cancelHideTimer]);

    const release = useCallback(() => {
        if (!hoveringRef.current) {
            scheduleHide();
        }
    }, [hoveringRef, scheduleHide]);

    return { isVisible, reveal, cancelHideTimer, scheduleHide, pin, release };
}

type ThumbHoverHandlers = Pick<OverlayScrollbarThumbHandlers, 'onPointerEnter' | 'onPointerLeave'>;

function useThumbHover(
    hoveringRef: FlagRef,
    dragRef: DragRef,
    fade: Pick<ThumbFade, 'pin' | 'scheduleHide'>
): ThumbHoverHandlers {
    const { pin, scheduleHide } = fade;

    const onPointerEnter = useCallback(() => {
        hoveringRef.current = true;
        pin();
    }, [hoveringRef, pin]);

    const onPointerLeave = useCallback(() => {
        hoveringRef.current = false;
        if (!dragRef.current) {
            scheduleHide();
        }
    }, [hoveringRef, dragRef, scheduleHide]);

    return { onPointerEnter, onPointerLeave };
}

interface GeometryPassParams {
    scrollEl: HTMLDivElement;
    adapter: OverlayScrollAxisAdapter;
    trackInsetPx: number;
    thumbRef: RefObject<HTMLDivElement>;
    geometryRef: GeometryRef;
    thumbEdgeRef: MutableRefObject<number>;
    setHasOverflow: (hasOverflow: boolean) => void;
}

// Style writes, not state: this runs once per scroll frame.
function createGeometryPass(params: GeometryPassParams): () => void {
    const { scrollEl, adapter, trackInsetPx, thumbRef, geometryRef, thumbEdgeRef, setHasOverflow } =
        params;
    return () => {
        const thumb = thumbRef.current;
        const metrics = adapter.readMetrics(scrollEl);
        thumbEdgeRef.current = adapter.thumbEdge(scrollEl.getBoundingClientRect());
        const layout = computeThumbLayout(
            metrics,
            trackInsetPx,
            MIN_THUMB_LENGTH_PX,
            OVERFLOW_EPSILON_PX
        );
        if (!layout || !thumb) {
            geometryRef.current = null;
            setHasOverflow(false);
            return;
        }
        adapter.paintThumb(thumb, layout.length, layout.offset);
        geometryRef.current = {
            maxScroll: layout.maxScroll,
            maxThumbOffset: layout.maxThumbOffset
        };
        setHasOverflow(true);
    };
}

interface ScrollListenerParams {
    update: () => void;
    reveal: () => void;
    adapter: OverlayScrollAxisAdapter;
    frameRef: MutableRefObject<number | null>;
    thumbEdgeRef: MutableRefObject<number>;
    geometryRef: GeometryRef;
}

interface ScrollListeners {
    onScroll: () => void;
    onPointerMove: (event: PointerEvent) => void;
}

function createScrollListeners(params: ScrollListenerParams): ScrollListeners {
    const { update, reveal, adapter, frameRef, thumbEdgeRef, geometryRef } = params;
    // One geometry pass per animation frame, however fast the wheel.
    const onScroll = () => {
        if (frameRef.current !== null) {
            return;
        }
        frameRef.current = window.requestAnimationFrame(() => {
            frameRef.current = null;
            update();
            reveal();
        });
    };
    const onPointerMove = (event: PointerEvent) => {
        const nearEdge = thumbEdgeRef.current - adapter.acrossAxis(event) <= EDGE_REVEAL_ZONE_PX;
        if (geometryRef.current && nearEdge) {
            reveal();
        }
    };
    return { onScroll, onPointerMove };
}

interface ScrollTrackingParams {
    scrollEl: HTMLDivElement | null;
    adapter: OverlayScrollAxisAdapter;
    trackInsetPx: number;
    thumbRef: RefObject<HTMLDivElement>;
    geometryRef: GeometryRef;
    reveal: () => void;
    cancelHideTimer: () => void;
}

function useScrollTracking(params: ScrollTrackingParams): boolean {
    const { scrollEl, adapter, trackInsetPx, thumbRef, geometryRef, reveal, cancelHideTimer } =
        params;
    const [hasOverflow, setHasOverflow] = useState(false);
    const frameRef = useRef<number | null>(null);
    // Cached so the edge-reveal pointermove handler never forces layout.
    const thumbEdgeRef = useRef(0);

    useEffect(() => {
        if (!scrollEl) {
            return undefined;
        }
        const update = createGeometryPass({
            scrollEl,
            adapter,
            trackInsetPx,
            thumbRef,
            geometryRef,
            thumbEdgeRef,
            setHasOverflow
        });
        const { onScroll, onPointerMove } = createScrollListeners({
            update,
            reveal,
            adapter,
            frameRef,
            thumbEdgeRef,
            geometryRef
        });
        update();
        const observer = observeRegionGrowth(scrollEl, update);
        scrollEl.addEventListener('scroll', onScroll, { passive: true });
        scrollEl.addEventListener('pointermove', onPointerMove, { passive: true });
        return () => {
            observer.disconnect();
            scrollEl.removeEventListener('scroll', onScroll);
            scrollEl.removeEventListener('pointermove', onPointerMove);
            if (frameRef.current !== null) {
                window.cancelAnimationFrame(frameRef.current);
                frameRef.current = null;
            }
            cancelHideTimer();
        };
    }, [scrollEl, adapter, trackInsetPx, thumbRef, geometryRef, reveal, cancelHideTimer]);

    return hasOverflow;
}

// The region resizing changes the track; its single child growing changes the scroll size.
function observeRegionGrowth(scrollEl: HTMLElement, onResize: () => void): ResizeObserver {
    const observer = new ResizeObserver(onResize);
    observer.observe(scrollEl);
    const content = scrollEl.firstElementChild;
    if (content) {
        observer.observe(content);
    }
    return observer;
}

interface ThumbDragParams {
    scrollEl: HTMLDivElement | null;
    adapter: OverlayScrollAxisAdapter;
    geometryRef: GeometryRef;
    dragRef: DragRef;
    pin: () => void;
    release: () => void;
}

// No text selection / native drag while the thumb is held.
function captureThumbPointer(
    event: ReactPointerEvent<HTMLDivElement>,
    scrollEl: HTMLDivElement,
    adapter: OverlayScrollAxisAdapter
): DragState {
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    return {
        pointerId: event.pointerId,
        startPointer: adapter.alongAxis(event),
        startOffset: adapter.readMetrics(scrollEl).offset
    };
}

function releaseThumbPointer(event: ReactPointerEvent<HTMLDivElement>): void {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
    }
}

type ThumbDragHandlers = Omit<OverlayScrollbarThumbHandlers, 'onPointerEnter' | 'onPointerLeave'>;

function useThumbDrag(params: ThumbDragParams): ThumbDragHandlers {
    const { scrollEl, adapter, geometryRef, dragRef, pin, release } = params;

    const onPointerDown = useCallback(
        (event: ReactPointerEvent<HTMLDivElement>) => {
            if (!scrollEl || !geometryRef.current) {
                return;
            }
            dragRef.current = captureThumbPointer(event, scrollEl, adapter);
            pin();
        },
        [scrollEl, adapter, geometryRef, dragRef, pin]
    );

    const onPointerMove = useCallback(
        (event: ReactPointerEvent<HTMLDivElement>) => {
            const drag = dragRef.current;
            const geometry = geometryRef.current;
            if (!scrollEl || !drag || !geometry || drag.pointerId !== event.pointerId) {
                return;
            }
            if (geometry.maxThumbOffset <= 0) {
                return;
            }
            const delta = adapter.alongAxis(event) - drag.startPointer;
            // The browser clamps the scroll offset to [0, max] for us.
            adapter.scrollTo(
                scrollEl,
                drag.startOffset + delta * (geometry.maxScroll / geometry.maxThumbOffset)
            );
        },
        [scrollEl, adapter, geometryRef, dragRef]
    );

    const endDrag = useCallback(
        (event: ReactPointerEvent<HTMLDivElement>) => {
            const drag = dragRef.current;
            if (!drag || drag.pointerId !== event.pointerId) {
                return;
            }
            dragRef.current = null;
            releaseThumbPointer(event);
            release();
        },
        [dragRef, release]
    );

    return { onPointerDown, onPointerMove, onPointerUp: endDrag, onPointerCancel: endDrag };
}

export function useOverlayScrollbar(options: OverlayScrollbarOptions = {}): OverlayScrollbar {
    const trackInsetPx = options.trackInsetPx ?? DEFAULT_TRACK_INSET_PX;
    const axis = options.axis ?? 'y';
    const adapter = OVERLAY_SCROLL_AXES[axis];
    const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);
    const thumbRef = useRef<HTMLDivElement>(null);
    const geometryRef = useRef<ThumbGeometry | null>(null);
    const dragRef = useRef<DragState | null>(null);
    const hoveringRef = useRef(false);

    const fade = useThumbFade(geometryRef, dragRef, hoveringRef);
    const { reveal, cancelHideTimer, pin, release } = fade;
    const hoverHandlers = useThumbHover(hoveringRef, dragRef, fade);
    const hasOverflow = useScrollTracking({
        scrollEl,
        adapter,
        trackInsetPx,
        thumbRef,
        geometryRef,
        reveal,
        cancelHideTimer
    });
    const dragHandlers = useThumbDrag({ scrollEl, adapter, geometryRef, dragRef, pin, release });

    return {
        setScrollElement: setScrollEl,
        scrollElement: scrollEl,
        axis,
        thumbRef,
        hasOverflow,
        isVisible: fade.isVisible,
        thumbHandlers: { ...dragHandlers, ...hoverHandlers }
    };
}
